import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { useReviewPreviewPageStatus, useReviewPreviewParams } from 'screens/performanceReview/hooks/reviewPreviewHooks';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';

import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getGoalForm } from 'services/performanceReview';

export const flattenGoalQuestions = (goalQuestions = []) => {
  const flatQuestions = [];
  for (let i = 0; i < goalQuestions.length; i += 1) {
    const { goal, questions, okr } = goalQuestions[i];
    for (let j = 0; j < questions.length; j += 1) {
      flatQuestions.push({
        goal,
        okr,
        question: questions[j],
      });
    }
  }

  return flatQuestions;
};

export const mergeGoalQuestionsByGoalId = (flatGoalQuestion) => {
  const isGoalAssociatedQuestions = !!flatGoalQuestion?.[0]?.goal?.id;

  if (!isGoalAssociatedQuestions) {
    return flatGoalQuestion.map((obj) => ({ ...obj, questions: [obj.question] }));
  }

  const groupedQuestionsByGoal = {};
  const order = [];

  flatGoalQuestion.forEach((obj) => {
    const goalId = obj.goal.id;
    if (!groupedQuestionsByGoal[goalId]) {
      groupedQuestionsByGoal[goalId] = { ...obj, questions: [obj.question] };
      order.push(goalId);
    } else {
      groupedQuestionsByGoal[goalId].questions.push(obj.question);
    }
  });

  return order.map((id) => groupedQuestionsByGoal[id]);
};

const getData = async ({
  reviewId,
  employeeId,
  providerId,
  responseId,
  providerType,
  isPreview,
  previewSubmissionStatus,
}) => {
  try {
    const res = await getGoalForm({
      reviewId,
      employeeId,
      providerId,
      responseId,
      providerType,
      isPreview,
      previewSubmissionStatus,
    });

    const goalQuestions = res?.goalQuestions ?? [];
    const flatGoalQuestion = flattenGoalQuestions(goalQuestions);
    // merge goal questions by goal id if applicable
    const mergedGoalQuestions = mergeGoalQuestionsByGoalId(flatGoalQuestion);

    return { ...res, mergedGoalQuestions };
  } catch (err) {
    console.error('Error fetching goal form data : ', err);
  }
  return null;
};

export const useGoalFormData = ({ reviewId, employeeId, providerId, responseId }) => {
  const { isPreview, providerType } = useReviewPreviewParams();
  const previewSubmissionStatus = useReviewPreviewPageStatus();

  return useQuery(
    [REACT_QUERY_KEYS.ReviewGoalFormData, reviewId, employeeId, providerId, responseId],
    () =>
      getData({
        reviewId,
        employeeId,
        providerId,
        responseId,
        providerType,
        isPreview,
        previewSubmissionStatus,
      }),
    { enabled: !!employeeId }
  );
};

export const useInvalidateGoalFormData = ({ reviewId, employeeId, providerId, responseId }) => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [REACT_QUERY_KEYS.ReviewGoalFormData, reviewId, employeeId, providerId, responseId],
    });
};

export const useGoalFormMutation = ({ answerQuestion }) => {
  const queryClient = useQueryClient();
  const { employeeId, providerId, reviewId, responseId } = useReviewFormParams();
  const { isPreview } = useReviewPreviewParams();

  return useMutation(answerQuestion, {
    onMutate: async ({ feedbackText, feedbackTextHtml, selectedAnswerOptions, goalQuestionsIndex, questionIndex }) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.ReviewGoalFormData, reviewId, employeeId, providerId, responseId],
      });

      // Snapshot the previous value
      const questionData = queryClient.getQueryData([
        REACT_QUERY_KEYS.ReviewGoalFormData,
        reviewId,
        employeeId,
        providerId,
        responseId,
      ]);

      // Optimistically update to the new value
      queryClient.setQueryData(
        [REACT_QUERY_KEYS.ReviewGoalFormData, reviewId, employeeId, providerId, responseId],
        (old) => {
          const updatedData = produce(old, (draft) => {
            // eslint-disable-next-line no-param-reassign
            draft.mergedGoalQuestions[goalQuestionsIndex].questions[questionIndex] = {
              ...draft.mergedGoalQuestions[goalQuestionsIndex]?.questions[questionIndex],
              feedbackText,
              feedbackTextHtml,
              selectedAnswerOptions,
            };
          });
          return updatedData;
        }
      );

      // Return a context object with the snapshotted value
      return { questionData };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (err, newQuestion, context) => {
      queryClient.setQueryData(
        [REACT_QUERY_KEYS.ReviewGoalFormData, reviewId, employeeId, providerId, responseId],
        context.questionData
      );
    },
    // Always refetch after error or success:
    onSettled: () => {
      if (isPreview) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.ReviewGoalFormData, reviewId, employeeId, providerId, responseId],
      });
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.ReviewFormFillingRatingData, reviewId, employeeId, providerId],
      });
      // used to refetch sidebar data
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.ReviewFormSidebar, employeeId, reviewId, providerId],
      });
    },
  });
};
